import { environment } from '@environments/environment';
import { BidEstimateService } from '@http/clients/reporting-app/bid-estimates.service';
import { TabNames } from '@models/enums/TabNames';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { WidgetType } from '@pages/reports/types/report-widget-type.enum';
import { formatNumbersCompact, percentageFormatter, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import {
    accountEditorConfig,
    bidAmountEditorConfig,
    bidDateEditorConfig,
    customerEditorConfig,
    departmentEditorConfig,
    estimateTypeEditorConfig,
    includeArchivedEditorConfig,
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    opportunityStatusEditorConfig,
    probabilityEditorConfig
} from './shared/additional-filters';
import { crmMainFilters } from './shared/main-filters';

export const EstimatesBid: IReportConfiguration<BidEstimateService> = {
    base: {
        type: ReportType.EstimateBid,
        serviceType: BidEstimateService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getEstimateBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getEstimateMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Average' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        { name: 'BidAmount', label: 'Bid Amount', format: 'currency' },
                        { name: 'GrossMarginPercentage', label: 'Gross Margin (%)', format: 'customPercent' }
                    ]
                }
            },
            {
                methodName: 'getEstimateLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Estimates by Bid Amount'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'estimateName',
                            yKey: 'totalBidAmount',
                            yName: 'Bid Amount'
                        },
                        {
                            type: 'line',
                            xKey: 'estimateName',
                            yKey: 'totalGrossMarginPercentage',
                            yName: 'Gross Margin (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalBidAmount'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['totalGrossMarginPercentage'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getEstimateColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'totalBidAmount',
                            yName: 'Bid Amount'
                        },
                        {
                            type: 'line',
                            xKey: 'salesPerson',
                            yKey: 'totalGrossMarginPercentage',
                            yName: 'Gross Margin (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            keys: ['totalBidAmount'],
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            keys: ['totalGrossMarginPercentage'],
                            title: {
                                text: 'Percentage'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getEstimateValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'baseEstimateId',
                        headerName: 'BID',
                        lockVisible: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (!params.value) {
                                return;
                            }
                            if (params.data?.projectArchived) {
                                return params.value;
                            } else {
                                const link = `${environment.SalesAppClientBaseUrl}/${environment.SalesAppClientProjectOverviewUrl}/${params.value}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                        }
                    },
                    { field: 'estimateName', headerName: 'Estimate Name', enableRowGroup: true },
                    { field: 'estimateType', headerName: 'Estimate Type', enableRowGroup: true },
                    { field: 'bidDate', headerName: 'Bid Date', type: 'date' },
                    {
                        field: 'oid',
                        headerName: 'OID',
                        cellRenderer: (params: ICellRendererParams) => {
                            if (params.value) {
                                const link = `/opportunities-single/${params.value}/${TabNames[TabNames.Overview]}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                            return '';
                        }
                    },
                    { field: 'opportunityName', headerName: 'Opportunity Name', enableRowGroup: true },
                    { field: 'customer', headerName: 'Customer', enableRowGroup: true },
                    { field: 'account', headerName: 'Account', enableRowGroup: true },
                    { field: 'bidAmount', headerName: 'Bid Amount', type: 'currency', aggFunc: 'sum' },
                    {
                        field: 'grossMarginPercentage',
                        headerName: 'Gross Margin (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'division', headerName: 'Department', enableRowGroup: true },
                    { field: 'salesPerson', headerName: 'Salesperson', enableRowGroup: true },
                    { field: 'status', headerName: 'Opportunity Status', enableRowGroup: true },
                    { field: 'market', headerName: 'Market' },
                    { field: 'leadSource', headerName: 'Opportunity Source', enableRowGroup: true },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    { field: 'probability', headerName: 'Probability', type: 'percentageString', aggFunc: 'avg' },
                    { field: 'condition', headerName: 'Condition', enableRowGroup: true },
                    {
                        field: 'opportunityLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'opportunityLastUpdatedNote'
                    }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...crmMainFilters],
        additional: [
            bidDateEditorConfig,
            bidAmountEditorConfig,
            estimateTypeEditorConfig,
            opportunityStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig,
            departmentEditorConfig,
            customerEditorConfig,
            accountEditorConfig,
            probabilityEditorConfig,
            includeArchivedEditorConfig
        ]
    }
};
